<template>
  <div class="pb-12">
    <v-progress-linear class="section-progress-stepper" :value="progressCount" />
    <v-row>
      <v-col cols="12" md="6" class="pl-md-10">
        <transition-group
          enter-active-class="fadeIn"
          leave-active-class="fadeOutFast"
          mode="out-in"
        >
          <template v-for="(question, index) in questions">
            <div
              v-if="checkVisible(question)"
              :id="'question-' + question.id"
              :key="question.id"
              class="mb-5 question-box d-flex align-center justify-center"
            >
              <radio-question
                v-if="question.type === 'radio'"
                :question="question"
                :current-answer="getAnswer(question)"
                @selectAnswer="addAnswer($event, index)"
              />
              <dropdown-question
                v-else-if="question.type === 'dropdown'"
                :question="question"
                :current-answer="getAnswer(question)"
                :parent-answer="getParentAnswer(question)"
                :answers="answers"
                @selectAnswer="addAnswer($event, index)"
              />
              <multiple-question
                v-else-if="question.type === 'multiple'"
                :question="question"
                :current-answer="getAnswer(question)"
                :parent-answer="getParentAnswer(question)"
                :answers="answers"
                @updateAnswer="addAnswer($event, index)"
                @removeAnswer="removeAnswer"
              />
            </div>
          </template>
        </transition-group>
      </v-col>
      <v-col cols="12" md="6" class="d-flex align-center justify-center">
        <transition
          enter-active-class="fadeIn"
          leave-active-class="fadeOut"
          mode="out-in"
        >
          <div
            v-if="completedSection"
            id="total-section"
            :key="'total-amount'"
            class="d-flex flex-column justify-center align-center"
          >
            <div class="mb-3">
              Pretul pentru asigurare <strong>RCA</strong>:
            </div>
            <div class="total-amount">
              {{ totalAmount }} LEI
            </div>
            <div class="mt-5">
              <v-btn
                color="#df0100"
                class="base-button"
                depressed
                tile
                dark
                large
                @click="$emit('nextStep', dataLayerEvent)"
              >
                Comanda asigurare
                <v-icon right>
                  mdi-arrow-right-circle
                </v-icon>
              </v-btn>
            </div>
          </div>
        </transition>
      </v-col>
    </v-row>
    <div id="bottom"></div>
  </div>
</template>

<script>
import { bonusMalusMultipliers, CONSTANTS, getQuestions } from '@/_utils/static-data';
import RadioQuestion from '@/components/RadioQuestion';
import DropdownQuestion from '@/components/DropdownQuestion';
import MultipleQuestion from '@/components/MultipleQuestion';
export default {
  name: 'RcaCalculator',
  components: {
    MultipleQuestion,
    DropdownQuestion,
    RadioQuestion
  },
  data() {
    return {
      answers: [],
      lastAnswerIndex: 1
    };
  },
  computed: {
    dataLayerEvent() {
      return CONSTANTS.rcaEventOrder;
    },
    questions() {
      const allQuestions = getQuestions('rca', 'calculator');
      let visibleQuestions = [];
      for (const question of allQuestions) {
        if (!question.parent) {
          visibleQuestions.push(question);
        } else if (question.parent && question.visibleIf) {
          const parentAnswer = this.answers.find(el => el.questionId === question.visibleIf.questionId);
          if (parentAnswer?.id === question.visibleIf.answer) {
            visibleQuestions.push(question);
          }
        } else if (question.parent && !question.visibleIf) {
          const parentAnswer = this.answers.find(el => el.questionId === question.parent);
          if (parentAnswer) {
            visibleQuestions.push(question);
          }
        }
      }

      // SPECIAL CASE FOR EXTERNAL CARS
      const firstAnswer = this.answers.find(el => el.questionId === 1);
      if (firstAnswer?.id === 2) {
        visibleQuestions = visibleQuestions.filter(el => el.id !== 7 && el.id !== 8);
      }
      return visibleQuestions;
    },
    progressCount() {
      return (this.answers.length / this.totalQuestions) * 100;
    },
    totalQuestions() {
      return this.questions.length;
    },
    isMobile() {
      return this.$vuetify.breakpoint.width < 961;
    },
    totalAmount() {
      const defaultRca = this.calculateDefaultRca(this.answers.filter(el => !el.isBonus));
      const bonusMalusClass = this.calculateBonusMalus(this.answers);
      const q = this.getBonusMalusMultiplier(bonusMalusClass);
      return parseFloat(defaultRca * q).toFixed(2);
    },
    completedSection() {
      return this.totalQuestions === this.answers.length;
    }
  },
  methods: {
    calculateDefaultRca(answers) {
      let total = 1350;
      for (const answer of answers) {
        if (answer.value && answer.questionId !== 7 && answer.questionId !== 8) {
          total = answer.value * total;
        } else if (answer.questionId === 8) {
          // SPECIAL CASE: combination of multiple values
          const parentAnswer = answers.find(el => el.questionId === 6);
          if (parentAnswer?.id === 1) {
            const prevAnswer = this.answers.find(el => el.questionId === 7);
            if (answer.id === 3) {
              const additionalValue = (prevAnswer?.id === 1 || prevAnswer.id === 2) ? 1 : 0.9;
              total = total * answer.sumValue * additionalValue; // Pension
            } else {
              total = total * (answer.sumValue + prevAnswer.sumValue);
            }
          }
        }
        // Addition to Juridic
        if (answer.questionId === 2 && answer.id === 2) {
          total = total * 1.2;
        }
      }
      return total;
    },
    calculateBonusMalus(answers) {
      let bonusMalusClass = 7;
      // HAS PREV RCA?
      const bonusQuestion = answers.find(el => el.questionId === 10);
      // ANSWER: NO
      if (bonusQuestion?.id === 1) {
        return bonusMalusClass;
      }
      const bonus = this.calculateBonus(answers);
      const malus = this.calculateMalus(answers);

      bonusMalusClass = bonusMalusClass + malus + bonus;
      return (bonusMalusClass >= 0 && bonusMalusClass <= 17)
        ? bonusMalusClass
        : (bonusMalusClass < 0 ? 0 : 17);
    },
    calculateBonus(answers) {
      // PHYSICAL/LEGAL OWNER
      const typeOfOwnerAnswer = answers.find(el => el.questionId === 2) || 0;

      const licenseYears = typeOfOwnerAnswer?.id === 1 ? answers.find(el => el.questionId === 7)?.years || 0 : 11;
      // Bonus from 2014
      const yearsOfRca = answers.find(el => el.questionId === 12)?.ids || [];
      // Get only valid rca years based on current year and license years, not counting current year
      const currentYear = new Date().getFullYear();
      const yearsOfRcaBonus = yearsOfRca.filter(el => {
        return parseInt(el) >= currentYear - licenseYears && parseInt(el) < currentYear;
      }) || [];
      // If ONLY has RCA in 2014, then bonus based on prev to 2014 license years
      if (yearsOfRcaBonus.includes(2014)) {
        let minPossibleYear = currentYear - 1 - licenseYears;
        while (minPossibleYear < 2014) {
          yearsOfRcaBonus.push(minPossibleYear);
          minPossibleYear++;
        }
      }
      return Math.min(yearsOfRcaBonus.length, licenseYears);
    },
    calculateMalus(answers) {
      const prevAccidentsQuestion = answers.find(el => el.questionId === 11);
      return prevAccidentsQuestion?.bonusValue || 0;
    },
    getBonusMalusMultiplier(bonusMalusClass) {
      return bonusMalusMultipliers[bonusMalusClass];
    },
    getAnswer(question) {
      return this.answers.find(el => el.questionId === question.id);
    },
    // getAnswerValue(question) {
    //   const answer = this.getAnswer(question);
    //   return answer ? answer.value || answer.sumValue : 0;
    // },
    getParentAnswer(question) {
      return question.parent ? this.answers.find(el => el.questionId === question.parent) : null;
    },
    removeChildren(children) {
      if (!children) { return; }
      for (const childId of children) {
        this.removeAnswer(childId);
      }
    },
    checkVisible(question) {
      return this.lastAnswerIndex >= question.order && !question.hidden;
    },
    removeAnswer(questionId) {
      this.answers = this.answers.filter(el => el.questionId !== questionId);
      this.$emit('updateAnswers', {
        answers: JSON.parse(JSON.stringify(this.answers)),
        isValid: this.completedSection
      });
    },
    addAnswer(answer, index) {
      const isAnswered = !!this.answers.find(el => el.questionId === answer.questionId);
      const currentQuestion = this.questions.find(el => el.id === answer.questionId);

      // SPECIAL CASE 3: no nr of drivers question - automatic unlimited nr answer
      const allQuestions = getQuestions('rca', 'calculator');
      const qst = allQuestions.find(el => el.id === 6);
      const hiddenQuestionAnswer = {
        questionId: qst.id,
        questionText: qst.text,
        isBonus: qst.isBonus || false,
        ...qst.options[1]
      };

      if (isAnswered) {
        this.answers = this.answers.map((el) => {
          if (el.questionId === answer.questionId) {
            el = answer;
          }
          return el;
        });
        this.removeChildren(currentQuestion.children);
        // SPECIAL CASE 1: FOR EXTERNAL CARS
        if (answer.questionId === 1 && answer.id === 2) {
          this.removeChildren([7, 8]);
        }
        // SPECIAL CASE 2: NO BONUS questions for questionId 1 && answer 2
        if (answer.questionId === 1 && answer.id === 2) {
          this.removeChildren([10, 11, 12]);
        }
        // SPECIAL CASE 3: no nr of drivers question - automatic unlimited nr answer
        if (answer.questionId === 2 && answer.id === 1) {
          this.addAnswer(hiddenQuestionAnswer, 5);
        }
      } else {
        if (answer.questionId === 1 && !this.isMobile) {
          this.$emit('formStarted');
        }
        this.answers.push(answer);
        const nextQuestion = this.questions[index + 1];
        if (nextQuestion?.order > this.lastAnswerIndex) {
          this.lastAnswerIndex = nextQuestion.order;
        } else if (currentQuestion?.order >= this.lastAnswerIndex) {
          this.lastAnswerIndex = currentQuestion.order + 1;
        }
        if (this.isMobile && !this.completedSection) {
          this.scrollToNext(this.lastAnswerIndex);
        }
        // SPECIAL CASE 3: no nr of drivers question - automatic unlimited nr answer
        const isPhysicalOwner = !!this.answers.find(el => el.questionId === 2 && el.id === 1);
        if (answer.questionId === 5 && isPhysicalOwner) {
          this.addAnswer(hiddenQuestionAnswer, 5);
        }
      }
      setTimeout(() => {
        this.$emit('updateAnswers', {
          answers: JSON.parse(JSON.stringify(this.answers)),
          isValid: this.completedSection,
          total: this.totalAmount
        });
      }, 200);
      // event for price shown
      if (this.completedSection) {
        this.$emit('trackEvent', {
          eventName: CONSTANTS.rcaPriceShow
        });
      }
      if (this.completedSection && this.isMobile) {
        this.scrollToTotal();
      }
    },
    scrollToNext(nextId) {
      setTimeout(() => {
        this.$scrollTo(
          `#question-${nextId}`,
          400,
          {
            offset: -180
          });
      }, 400);
    },
    scrollToTotal() {
      setTimeout(() => {
        this.$scrollTo(
          '#total-section',
          400,
          {
            offset: -180
          });
      }, 400);
    }
  }
};
</script>

<style scoped>
.section-progress-stepper {
  position: absolute;
  top: 0;
  left: 0;
}
.total-amount {
  font-size: 32px;
  font-weight: 600;
}
.question-box {
  width: 100% !important;
  background-color: #E7EFFD;
  padding: 8px;
  border-radius: 4px 4px 4px 4px;
}
</style>
